import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';

function GuestPortalLogin() {
  const [email, setEmail] = useState('');
  const [bookingId, setBookingId] = useState('');
  const [loading, setLoading] = useState(false);
  const [showRecoveryModal, setShowRecoveryModal] = useState(false);
  const [recoveryData, setRecoveryData] = useState({
    guest_email: '',
    guest_name: '',
    guest_phone: '',
    check_in: ''
  });
  const [recoveryLoading, setRecoveryLoading] = useState(false);
  const [recoveryResults, setRecoveryResults] = useState(null);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Verify access
      const response = await axios.post(`${API}/guest/verify-access`, {
        guest_email: email,
        booking_id: bookingId
      });

      if (response.status === 200) {
        // Store credentials in localStorage
        localStorage.setItem('guest_email', email);
        localStorage.setItem('guest_booking_id', bookingId);
        toast.success('Access verified! Redirecting to your bookings...');
        setTimeout(() => {
          navigate('/guest/dashboard');
        }, 1000);
      }
    } catch (error) {
      console.error('Guest portal access error:', error);
      if (error.response?.status === 404) {
        toast.error('No booking found with this email and booking ID');
      } else {
        toast.error('Failed to verify access. Please check your credentials.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRecoverySubmit = async (e) => {
    e.preventDefault();
    setRecoveryLoading(true);
    setRecoveryResults(null);

    try {
      const response = await axios.post(`${API}/guest/recover-booking`, recoveryData);
      
      if (response.data.bookings && response.data.bookings.length > 0) {
        setRecoveryResults(response.data.bookings);
        toast.success(`Found ${response.data.count} booking(s)!`);
      } else {
        toast.error('No bookings found with the provided information');
      }
    } catch (error) {
      console.error('Recovery error:', error);
      if (error.response?.status === 404) {
        toast.error('No bookings found with the provided information');
      } else if (error.response?.status === 400) {
        toast.error(error.response.data.detail || 'Please provide valid information');
      } else {
        toast.error('Failed to search for bookings. Please try again.');
      }
    } finally {
      setRecoveryLoading(false);
    }
  };

  const useBookingId = (bookingId) => {
    setBookingId(bookingId);
    setEmail(recoveryData.guest_email);
    setShowRecoveryModal(false);
    setRecoveryResults(null);
    toast.success('Booking ID filled in! You can now access your portal.');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Guest Portal</h1>
          <p className="text-gray-600">Access your bookings</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Address
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your.email@example.com"
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Booking ID
            </label>
            <input
              type="text"
              value={bookingId}
              onChange={(e) => setBookingId(e.target.value)}
              placeholder="Enter your booking ID"
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="mt-2 text-sm text-gray-500">
              You can find your booking ID in your confirmation email
            </p>
            <button
              type="button"
              onClick={() => setShowRecoveryModal(true)}
              className="mt-2 text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              Forgot your Booking ID?
            </button>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {loading ? 'Verifying...' : 'Access Portal'}
          </button>
        </form>

        <div className="mt-6 text-center">
          <button
            onClick={() => navigate('/')}
            className="text-blue-600 hover:text-blue-700 text-sm font-medium"
          >
            ← Back to Home
          </button>
        </div>
      </div>

      {/* Recovery Modal */}
      {showRecoveryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center px-4 z-50">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">Recover Booking ID</h2>
              <button
                onClick={() => {
                  setShowRecoveryModal(false);
                  setRecoveryResults(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            {!recoveryResults ? (
              <form onSubmit={handleRecoverySubmit} className="space-y-4">
                <p className="text-sm text-gray-600 mb-4">
                  Enter your email and at least one additional piece of information to find your booking.
                </p>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    value={recoveryData.guest_email}
                    onChange={(e) => setRecoveryData({...recoveryData, guest_email: e.target.value})}
                    placeholder="your.email@example.com"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Guest Name
                  </label>
                  <input
                    type="text"
                    value={recoveryData.guest_name}
                    onChange={(e) => setRecoveryData({...recoveryData, guest_name: e.target.value})}
                    placeholder="Full name or partial name"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={recoveryData.guest_phone}
                    onChange={(e) => setRecoveryData({...recoveryData, guest_phone: e.target.value})}
                    placeholder="Phone number (last 4 digits work too)"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Check-in Date
                  </label>
                  <input
                    type="date"
                    value={recoveryData.check_in}
                    onChange={(e) => setRecoveryData({...recoveryData, check_in: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowRecoveryModal(false);
                      setRecoveryResults(null);
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={recoveryLoading}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
                  >
                    {recoveryLoading ? 'Searching...' : 'Find Booking'}
                  </button>
                </div>
              </form>
            ) : (
              <div>
                <p className="text-sm text-gray-600 mb-4">
                  Found {recoveryResults.length} booking(s). Click on a booking to use its ID:
                </p>
                
                <div className="space-y-3">
                  {recoveryResults.map((booking, index) => (
                    <div
                      key={index}
                      className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50 cursor-pointer"
                      onClick={() => useBookingId(booking.booking_id)}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-gray-900">{booking.guest_name}</p>
                          <p className="text-sm text-gray-600">
                            {booking.check_in} to {booking.check_out}
                          </p>
                          <p className="text-sm text-gray-500">
                            Total: €{booking.total_amount} • Status: {booking.payment_status}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-gray-500">Booking ID:</p>
                          <p className="text-sm font-mono text-blue-600">{booking.booking_id}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <button
                  onClick={() => {
                    setRecoveryResults(null);
                    setRecoveryData({
                      guest_email: '',
                      guest_name: '',
                      guest_phone: '',
                      check_in: ''
                    });
                  }}
                  className="w-full mt-4 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Search Again
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default GuestPortalLogin;
